import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, X, Star, ImagePlus, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { photoUrl } from "@/components/PhotoUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Per-animal photo gallery — add, set primary, remove, drag-to-reorder. */
export const PhotoGallery = ({ animalId, primary, paths = [], onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragSrc, setDragSrc] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/animals/${animalId}/gallery`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ primary: res.data.path || primary || res.data.photo_paths?.[0] || "", paths: res.data.photo_paths || [] });
      toast.success("Photo added");
    } catch (err) {
      // Surface the actual backend message so we don't swallow real failures
      // (storage timeout, auth expired, file rejected by Emergent storage…).
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = typeof detail === "string" ? detail : (err?.message || "Upload failed");
      toast.error(status ? `Upload failed (${status}): ${msg}` : `Upload failed: ${msg}`);
    }
    setBusy(false);
  };

  const setPrimary = async (path) => {
    try {
      await axios.post(`${API}/animals/${animalId}/gallery/primary?path=${encodeURIComponent(path)}`);
      onChange({ primary: path, paths });
      toast.success("Primary photo updated");
    } catch { toast.error("Couldn't set primary"); }
  };

  const remove = async (path) => {
    try {
      const res = await axios.delete(`${API}/animals/${animalId}/gallery?path=${encodeURIComponent(path)}`);
      onChange({ primary: res.data.primary || "", paths: res.data.photo_paths || [] });
    } catch { toast.error("Couldn't remove"); }
  };

  const commitReorder = async (next) => {
    onChange({ primary, paths: next }); // optimistic
    try {
      await axios.put(`${API}/animals/${animalId}/gallery/order`, { paths: next });
    } catch {
      toast.error("Couldn't save new order");
      onChange({ primary, paths }); // revert
    }
  };

  const onDragStart = (e, path) => {
    setDragSrc(path);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to be set for a drag to start
    e.dataTransfer.setData("text/plain", path);
  };
  const onDragEnter = (path) => { if (dragSrc && path !== dragSrc) setDragOver(path); };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, target) => {
    e.preventDefault();
    const src = dragSrc;
    setDragSrc(null); setDragOver(null);
    if (!src || src === target) return;
    const next = [...paths];
    const fromIdx = next.indexOf(src);
    const toIdx = next.indexOf(target);
    if (fromIdx < 0 || toIdx < 0) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, src);
    commitReorder(next);
  };
  const onDragEnd = () => { setDragSrc(null); setDragOver(null); };

  return (
    <div className="flex flex-wrap gap-3" data-testid="photo-gallery">
      {paths.map((p) => {
        const isPrimary = p === primary;
        const isDragging = dragSrc === p;
        const isTarget = dragOver === p;
        return (
          <div
            key={p}
            className={`relative w-20 h-20 group transition ${isDragging ? "opacity-40" : ""} ${isTarget ? "scale-105" : ""}`}
            data-testid="gallery-thumb"
            data-path={p}
            draggable
            onDragStart={(e) => onDragStart(e, p)}
            onDragEnter={() => onDragEnter(p)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, p)}
            onDragEnd={onDragEnd}
          >
            <img
              src={photoUrl(p)}
              alt="gallery"
              className={`w-20 h-20 object-cover rounded border pointer-events-none ${isPrimary ? "border-blue-500 ring-2 ring-blue-200" : isTarget ? "border-blue-400 ring-2 ring-blue-100" : "border-[var(--app-border)]"}`}
              draggable={false}
            />
            {isPrimary && (
              <div className="absolute top-1 left-1 bg-blue-600 text-white rounded-full p-0.5 pointer-events-none" data-testid="thumb-primary-badge">
                <Star className="w-3 h-3" fill="currentColor" />
              </div>
            )}
            <div className="absolute top-1 right-1 bg-white/85 rounded p-0.5 opacity-0 group-hover:opacity-100 transition pointer-events-none" title="Drag to reorder">
              <GripVertical className="w-3 h-3 text-[var(--app-text-soft)]" />
            </div>
            <div className="absolute inset-0 rounded bg-black/55 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
              {!isPrimary && (
                <button type="button" onClick={() => setPrimary(p)} title="Make primary" className="p-1 rounded bg-white/95 hover:bg-white" data-testid="thumb-set-primary">
                  <Star className="w-3.5 h-3.5 text-blue-600" />
                </button>
              )}
              <button type="button" onClick={() => remove(p)} title="Remove" className="p-1 rounded bg-white/95 hover:bg-white" data-testid="thumb-remove">
                <X className="w-3.5 h-3.5 text-red-600" />
              </button>
            </div>
          </div>
        );
      })}

      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="gallery-input" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="w-20 h-20 rounded border-2 border-dashed border-[var(--app-border)] hover:border-blue-400 flex flex-col items-center justify-center text-[var(--app-text-soft)] hover:text-blue-600 transition"
        data-testid="gallery-add-btn"
      >
        {paths.length === 0 ? <Upload className="w-5 h-5" strokeWidth={1.4} /> : <ImagePlus className="w-5 h-5" strokeWidth={1.4} />}
        <span className="text-[10px] mt-0.5">{busy ? "Adding…" : paths.length === 0 ? "Upload" : "Add"}</span>
      </button>

      <div className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[var(--app-text-soft)] self-center hidden md:block">
        {paths.length === 0
          ? "No photos yet"
          : `${paths.length} photo${paths.length === 1 ? "" : "s"} · drag to reorder · ★ to set primary`}
      </div>
    </div>
  );
};

export default PhotoGallery;
